/**
 * App Versions Table Component
 *
 * Displays a table of app versions with their information, status badges, and actions.
 * Supports loading states, empty states, error handling, and row selection.
 */

import { useState } from 'react';
import { AlertCircle, Loader2, MoreVertical, Trash2, Copy, Clock } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppVersionDeleteDialog } from './app-version-delete-dialog';
import { AppVersionDuplicateDialog } from './app-version-duplicate-dialog';
import { AppVersionForceUpdateDialog } from './app-version-force-update-dialog';
import type { AppVersion, Platform, Priority } from '@/types/app-version';

interface AppVersionsTableProps {
  versions: AppVersion[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onVersionClick: (version: AppVersion) => void;
  selectedVersions: string[];
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Format Date
 * Formats an ISO date string to a readable format
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return 'Not set';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get Platform Badge Variant
 */
function getPlatformBadgeVariant(platform: Platform): 'default' | 'secondary' | 'outline' {
  switch (platform) {
    case 'ios':
      return 'default';
    case 'android':
      return 'secondary';
    case 'both':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Get Priority Badge Variant
 */
function getPriorityBadgeVariant(
  priority: Priority
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (priority) {
    case 'critical':
      return 'destructive';
    case 'high':
      return 'default';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * App Versions Table
 * Renders a table of app versions with status badges, selection, and actions
 */
export function AppVersionsTable({
  versions,
  isLoading,
  error,
  onRetry,
  onVersionClick,
  selectedVersions,
  onSelectionChange,
}: AppVersionsTableProps) {
  const [deleteDialogVersion, setDeleteDialogVersion] = useState<AppVersion | null>(null);
  const [duplicateDialogVersion, setDuplicateDialogVersion] = useState<AppVersion | null>(null);
  const [forceUpdateDialogVersion, setForceUpdateDialogVersion] = useState<AppVersion | null>(null);

  // Handle select all
  const allSelected = versions.length > 0 && selectedVersions.length === versions.length;
  const someSelected = selectedVersions.length > 0 && selectedVersions.length < versions.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(versions.map(v => v.id));
    }
  };

  const handleSelectVersion = (versionId: string) => {
    if (selectedVersions.includes(versionId)) {
      onSelectionChange(selectedVersions.filter(id => id !== versionId));
    } else {
      onSelectionChange([...selectedVersions, versionId]);
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-2">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading app versions...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Failed to load app versions</h3>
          <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
        </div>
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty State
  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-2">
        <p className="text-sm text-muted-foreground">No app versions found</p>
        <p className="text-xs text-muted-foreground">Create a new version to get started</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  className={someSelected ? 'data-[state=checked]:bg-primary/50' : ''}
                />
              </TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Mandatory</TableHead>
              <TableHead>Force Update After</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.map(version => (
              <TableRow
                key={version.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onVersionClick(version)}
              >
                <TableCell onClick={e => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedVersions.includes(version.id)}
                    onCheckedChange={() => handleSelectVersion(version.id)}
                    aria-label={`Select version ${version.version}`}
                  />
                </TableCell>
                <TableCell className="font-mono font-medium">{version.version}</TableCell>
                <TableCell>
                  <Badge variant={getPlatformBadgeVariant(version.platform)}>
                    {version.platform.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={getPriorityBadgeVariant(version.priority)}>
                    {version.priority}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={version.isActive ? 'default' : 'secondary'}>
                    {version.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={version.isMandatory ? 'destructive' : 'outline'}>
                    {version.isMandatory ? 'Yes' : 'No'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">{formatDate(version.forceUpdateAfter)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(version.createdAt)}
                </TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setForceUpdateDialogVersion(version)}>
                        <Clock className="h-4 w-4 mr-2" />
                        Force Update
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDuplicateDialogVersion(version)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteDialogVersion(version)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Dialog */}
      <AppVersionDeleteDialog
        version={deleteDialogVersion}
        onClose={() => setDeleteDialogVersion(null)}
      />

      {/* Duplicate Dialog */}
      <AppVersionDuplicateDialog
        version={duplicateDialogVersion}
        onClose={() => setDuplicateDialogVersion(null)}
      />

      {/* Force Update Dialog */}
      <AppVersionForceUpdateDialog
        version={forceUpdateDialogVersion}
        onClose={() => setForceUpdateDialogVersion(null)}
      />
    </>
  );
}
